import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import ThemeToggle from "../../common/ThemeToggle/ThemeToggle";
import GlobalSearchInput from "../../common/GlobalSearchInput/GlobalSearchInput";
import styles from "./AppNavbar.module.css";

export default function AppNavbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const name = user?.displayName || user?.email || "Account";
  const photo = user?.photoURL || "";

  useEffect(() => {
    const isEditableTarget = (t) => {
      if (!t) return false;
      if (t.isContentEditable) return true;
      const tag = (t.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select";
    };

    const focusGlobalSearch = () => {
      // Try several stable selectors; succeed on the first match.
      const candidates = [
        // Preferred: explicit id on the actual input (add this in GlobalSearchInput if possible)
        '#pp-global-search-input',
        // Wrapper anchor → any input/combobox within
        '#pp-global-search-anchor input',
        '#pp-global-search-anchor [role="combobox"]',
        // Reasonable fallbacks by ARIA/placeholder/name
        'input[aria-label*="search" i]',
        'input[placeholder*="search" i]',
        'input[name="global-search"]',
      ];
      let el = null;
      for (const sel of candidates) {
        const found = document.querySelector(sel);
        if (found) { el = found; break; }
      }
      if (!el) return false;

      // Focus after this tick so we beat any other handlers
      setTimeout(() => {
        el.focus();
        if (typeof el.select === "function") el.select();
      }, 0);
      return true;
    };

    const handler = (e) => {
      // Ignore typing in fields & any modifiers
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;

      if (e.key === "/" || e.key === "?") {
        // Avoid browser quick-find (e.g. Firefox '/')
        e.preventDefault();
        focusGlobalSearch();
      }
    };

    // Use both events for better cross-browser coverage
    window.addEventListener("keydown", handler, { capture: true });
    window.addEventListener("keypress", handler, { capture: true });
    return () => {
      window.removeEventListener("keydown", handler, { capture: true });
      window.removeEventListener("keypress", handler, { capture: true });
    };
  }, []);

  return (
    <nav className={`navbar bg-body-tertiary border-bottom ${styles.nav}`}>
      <div className="container-fluid">
        {/* Hamburger shows only on <lg */}
        <button
          className="btn btn-outline-secondary d-lg-none me-2"
          type="button"
          data-bs-toggle="offcanvas"
          data-bs-target="#appSidebar"
          aria-controls="appSidebar"
          aria-label="Open navigation"
        >
          ☰
        </button>

        <Link
          className={`navbar-brand fw-bold d-flex align-items-center gap-2 ${styles.brand}`}
          to="/"
          aria-label="Pocket Penny — Home"
        >
          <img
            src="/pp-logo.png"
            alt=""
            className={styles.logoImg}
            height={28}
            width={28}
            aria-hidden="true"
          />
          <span className="fw-bold">Pocket&nbsp;Penny</span>
        </Link>

        {/* Anchor wrapper so the shortcut can query within here */}
        <div id="pp-global-search-anchor" className="flex-grow-1 mx-3" style={{ maxWidth: 620 }}>
          <GlobalSearchInput />
        </div>

        <div className="d-flex align-items-center gap-2">
          <ThemeToggle />

          {/* Profile dropdown */}
          <div className="dropdown">
            <button
              className={`btn btn-outline-secondary dropdown-toggle d-flex align-items-center gap-2 ${styles.profileBtn}`}
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {photo ? (
                <img src={photo} alt="Profile" className={styles.avatar} />
              ) : (
                <span className={styles.avatarFallback} aria-hidden>
                  {name?.[0]?.toUpperCase() || "U"}
                </span>
              )}
              <span className="d-none d-sm-inline">{name}</span>
            </button>

            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => navigate("/app/profile")}
                >
                  Manage profile
                </button>
              </li>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={async () => {
                    await logout();
                    navigate("/", { replace: true });
                  }}
                >
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
}
